import React, { useState } from 'react';
import { Button, TextField, Switch, FormControlLabel, InputAdornment, Box } from '@mui/material';
import {
  DataSourceCard,
  PremiumBadge,
  DataSourceIcon,
  DataSourceName,
  DataSourceDescription,
  DataSourceFooter,
  ConnectionStatus,
  StatusDot,
  SearchFilterSection,
  SearchBar,
  Filters,
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  ModalCloseButton,
  FormField,
  FormLabel,
  FormActions,
  PageContainer,
  DataSourcesGrid,
  StyledTabs,
  StyledTab
} from './DataSources.styles';
import { Gavel, Balance, School, AccountBalance, MenuBook, Analytics, Bolt, Search,
  Add, Close, WhatsApp, X, Telegram, Book, Email, Group, RssFeed, MobileFriendly, Facebook} from '@mui/icons-material';
import { PageHeader } from '@components/Shared/PageHeader';

// Mock data sources
const mockDataSources = [
  {
    id: 1,
    name: 'Law Africa',
    startIcon: <Gavel />,
    description: 'Access comprehensive legal research, including cases, statutes, and legal journals.',
    isConnected: true,
    isPremium: true,
  },
  {
    id: 2,
    name: 'LexisNexis',
    startIcon: <Balance />,
    description: 'Legal research database with extensive coverage of case law, statutes, and secondary sources.',
    isConnected: false,
    isPremium: true,
  },
  {
    id: 3,
    name: 'Google Scholar',
    startIcon: <School />,
    description: 'Search for scholarly articles, case law, and patents across various disciplines.',
    isConnected: true,
    isPremium: false,
  },
  {
    id: 4,
    name: 'PACER',
    startIcon: <AccountBalance />,
    description: 'Public Access to Court Electronic Records. Access federal court documents and case information.',
    isConnected: false,
    isPremium: false,
  },
  {
    id: 5,
    name: 'HeinOnline',
    startIcon: <MenuBook />,
    description: 'Database of legal history and government documents, including law journals and treaties.',
    isConnected: false,
    isPremium: true,
  },
  {
    id: 6,
    name: 'Bloomberg Law',
    startIcon: <Analytics />,
    description: 'Legal research platform with court dockets, legal analytics, and business intelligence.',
    isConnected: false,
    isPremium: true,
  },
  {
    id: 7,
    name: 'Casetext',
    startIcon: <Search />,
    description: 'AI-powered legal research tool with cases, statutes, and regulations.',
    isConnected: true,
    isPremium: true,
  },
  {
    id: 8,
    name: 'Fastcase',
    startIcon: <Bolt />,
    description: 'Legal research database with primary law, analytics, and workflow tools.',
    isConnected: false,
    isPremium: false,
  },
];

const mockConnector = [
  {
    id: 1,
    name: 'WhatsApp',
    startIcon: <WhatsApp />,
    description: 'Connect via WhatsApp to receive legal research insights, case updates, and journal digests.',
    isConnected: true,
    isPremium: true,
  },
  {
    id: 2,
    name: 'Slack',
    startIcon: <Book />,
    description: 'Integrate with Slack to collaborate on legal cases, share statutes, and access legal resources.',
    isConnected: false,
    isPremium: true,
  },
  {
    id: 3,
    name: 'Twitter',
    startIcon: <X />,
    description: 'Leverage Twitter to follow legal updates, case law trends, and scholarly commentary.',
    isConnected: true,
    isPremium: false,
  },
  {
    id: 4,
    name: 'Telegram',
    startIcon: <Telegram />,
    description: 'Access court documents and federal case info through Telegram integration.',
    isConnected: false,
    isPremium: false,
  },
  {
    id: 5,
    name: 'Email',
    startIcon: <Email />,
    description: 'Get legal history, government documents, and law journal updates delivered via email.',
    isConnected: false,
    isPremium: true,
  },
  {
    id: 6,
    name: 'Teams',
    startIcon: <Group />,
    description: 'Get legal history, government documents, and law journal updates delivered via Teams.',
    isConnected: false,
    isPremium: true,
  },
  {
    id: 7,
    name: 'Website',
    startIcon: <RssFeed />,
    description: 'Get legal history, government documents, and law journal updates delivered via Website.',
    isConnected: false,
    isPremium: true,
  },
  {
    id: 8,
    name: 'Mobile App',
    startIcon: <MobileFriendly />,
    description: 'Get legal history, government documents, and law journal updates delivered via Mobile App.',
    isConnected: false,
    isPremium: true,
  },
  {
    id: 9,
    name: 'Facebook',
    startIcon: <Facebook />,
    description: 'Connect via Facebook to receive legal research insights, case updates, and journal digests.',
    isConnected: true,
    isPremium: true,
  },
];

export default function DataSources() {
  const [showConnectedOnly, setShowConnectedOnly] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false);
  const [selectedDataSource, setSelectedDataSource] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  const rawDataSources = tabValue === 0 ? mockDataSources : mockConnector;

  const filteredDataSources = rawDataSources.filter(source => {
    const matchesSearch = source.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          source.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesConnectionFilter = showConnectedOnly ? source.isConnected : true;
    return matchesSearch && matchesConnectionFilter;
  });

  const handleToggleConnection = (id) => {
    if (tabValue === 0) {
      const source = mockDataSources.find(source => source.id === id);
      if (source.isConnected) {
        source.isConnected = false;
      } else {
        setSelectedDataSource(source);
        setIsConnectModalOpen(true);
      }
    } else {
      const source = mockConnector.find(source => source.id === id);
      if (source.isConnected) {
        source.isConnected = false;
      } else {
        setSelectedDataSource(source);
        setIsConnectModalOpen(true);
      }
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setSearchTerm(''); 
    setShowConnectedOnly(false);
  };

  const handleCloseConnectModal = () => {
    setIsConnectModalOpen(false);
    setSelectedDataSource(null);
  };

  const handleConnect = () => {
    const sourceList = tabValue === 0 ? mockDataSources : mockConnector;
    const source = sourceList.find(s => s.id === selectedDataSource.id);
    if (source) {
      source.isConnected = true;
    }
    handleCloseConnectModal();
  };
  
  return (
    <PageContainer>
      <PageHeader
        title="Data Sources"
        description="Connect different data sources to searches"
      />
      <SearchFilterSection>
        <SearchBar>
          <TextField
            fullWidth
            placeholder="Search data sources..."
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </SearchBar>
        
        <Filters>
          <FormControlLabel
            control={
              <Switch 
                checked={showConnectedOnly}
                onChange={() => setShowConnectedOnly(!showConnectedOnly)}
                color="primary"
              />
            }
            label="Connected only"
          />
          
          <Button
            startIcon={<Add /> }
            sx={{
              backgroundColor: (theme) => theme.palette.secondary.main,
              color: '#fff',
              '&:hover': {
                backgroundColor: (theme) => theme.palette.secondary.light,
              },
            }}
            variant="contained"
          >
            Add Custom Source
          </Button>
        </Filters>
      </SearchFilterSection>
      <StyledTabs value={tabValue} onChange={handleTabChange}>
          <StyledTab label="Data Sources" />
          <StyledTab label="Connectors" />
      </StyledTabs>
      
      <DataSourcesGrid>
        {filteredDataSources.map((source) => (
          <DataSourceCard 
            key={source.id} 
            isConnected={source.isConnected}
            isPremium={source.isPremium}
          >
            {source.isPremium && (
              <PremiumBadge>Premium</PremiumBadge>
            )}
            
            <DataSourceIcon>
              <Box
                component="span"
                className="material-icons"
                sx={{
                  fontSize: '28px',
                  color: (theme) => theme.palette.secondary.main,
                }}
              >
                {source.startIcon}
              </Box>
            </DataSourceIcon>
            
            <DataSourceName>{source.name}</DataSourceName>
            <DataSourceDescription>{source.description}</DataSourceDescription>
            
            <DataSourceFooter>
              <ConnectionStatus isConnected={source.isConnected}>
                <StatusDot isConnected={source.isConnected} />
                {source.isConnected ? 'Connected' : 'Not connected'}
              </ConnectionStatus>
              
              <Button 
                size="small"
                variant={source.isConnected ? "outlined" : "contained"}
                sx={
                  source.isConnected
                    ? {
                        borderColor: 'rgba(0, 0, 0, 0.2)',
                        color: 'rgba(0, 0, 0, 0.6)',
                      }
                    : {
                        backgroundColor: (theme) => theme.palette.secondary.main,
                        color: '#fff',
                        '&:hover': {
                          backgroundColor: (theme) => theme.palette.secondary.light,
                        },
                      }
                }
                onClick={() => handleToggleConnection(source.id)}
              >
                {source.isConnected ? 'Disconnect' : 'Connect'}
              </Button>
            </DataSourceFooter>
          </DataSourceCard>
        ))}
      </DataSourcesGrid>
      
      {/* Connect Modal */}
      {isConnectModalOpen && selectedDataSource && (
        <ModalOverlay>
          <ModalContainer>
            <ModalHeader>
              <ModalTitle>Connect to {selectedDataSource.name}</ModalTitle>
              <ModalCloseButton onClick={handleCloseConnectModal}>
                <Close />
              </ModalCloseButton>
            </ModalHeader>
            
            <p>Enter your credentials to connect to {selectedDataSource.name}.</p>
            
            <FormField>
              <FormLabel>API Key</FormLabel>
              <TextField 
                fullWidth
                placeholder="Enter API key"
                variant="outlined"
                size="small"
              />
            </FormField>
            
            {selectedDataSource.isPremium && (
              <FormField>
                <FormLabel>Account ID</FormLabel>
                <TextField 
                  fullWidth
                  placeholder="Enter your account ID"
                  variant="outlined"
                  size="small"
                />
              </FormField>
            )}
            
            <FormField>
              <FormControlLabel
                control={<Switch color="primary" />}
                label="Save credentials for future sessions"
              />
            </FormField>
            
            <FormActions>
              <Button onClick={handleCloseConnectModal}>
                Cancel
              </Button>
              <Button 
                variant="contained"
                sx={{
                  backgroundColor: (theme) => theme.palette.secondary.main,
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.secondary.light,
                  },
                }}
                onClick={handleConnect}
              >
                Connect
              </Button>
            </FormActions>
          </ModalContainer>
        </ModalOverlay>
      )}
    </PageContainer>
  );
}